import React, { useEffect, useState } from "react";
import Head from "next/head";
import { Document, Page, pdfjs } from "react-pdf";
import { PDFDocument } from "pdf-lib";
import PDFMerger from "pdf-merger-js/browser";
import { FaFileUpload } from "react-icons/fa";
import styles from "../../signature.module.css";
import Blog from "../../../../components/CalculatorBlogs/Blog";
import SignaturePopup from "../../../../components/SignaturePopup";
import PdfToolsLinks from "../../../../components/PdfToolsLinks";

pdfjs.GlobalWorkerOptions.workerSrc = `//cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjs.version}/pdf.worker.js`;

const BlogData = [
  {
    id: 1,
    question: "What is PDF Extract Pages Tool?",
    answer: (
      <div className="">
        <p className="">
          PDF Extract Pages Tool is an online tool that allows you to extract
          specific pages from a PDF file. This tool enables you to choose the
          pages you want to extract, providing a customized and streamlined
          approach to managing your PDF documents.
        </p>
      </div>
    ),
  },
  {
    id: 2,
    question: "How to Use PDF Extract Pages Tool?",
    answer: (
      <div className="">
        <p className="">
          Using the PDF Extract Pages Tool is simple and efficient. First, you
          upload the PDF file from which you want to extract pages. Then,
          specify the page numbers or range you wish to extract. The tool will
          process your request and generate a new PDF file containing only the
          selected pages.
        </p>
      </div>
    ),
  },
  {
    id: 3,
    question: "What Are the Benefits of Using PDF Extract Pages Tool?",
    answer: (
      <div className="">
        <p className="">
          The PDF Extract Pages Tool offers several advantages, including:
          <br />
          <ul className="list-disc list-inside">
            <li>
              Efficiency: Quickly extract specific pages without altering the
              original PDF.
            </li>
            <li>
              Customization: Select and extract pages based on your unique
              requirements.
            </li>
            <li>
              Accuracy: Ensure precise extraction of pages without any data
              loss.
            </li>
            <li>
              Convenience: User-friendly interface for seamless navigation and
              extraction.
            </li>
          </ul>
        </p>
      </div>
    ),
  },
  {
    id: 4,
    question: "Is PDF Extract Pages Tool Free to Use?",
    answer: (
      <div className="">
        <p className="">
          Yes, the PDF Extract Pages Tool is available for free. You can use
          this tool without any cost to extract pages from your PDF documents,
          making it a budget-friendly solution for your PDF management needs.
        </p>
      </div>
    ),
  },
  {
    id: 5,
    question: "Are Extracted PDFs Secure and Confidential?",
    answer: (
      <div className="">
        <p className="">
          Rest assured, the PDF Extract Pages Tool prioritizes your privacy and
          security. All uploaded files and extracted PDFs are treated with
          strict confidentiality. Your data is not shared or stored, ensuring a
          secure and private extraction process.
        </p>
      </div>
    ),
  },
];

const ExtractPdfPages = () => {
  const [pdfFile, setPdfFile] = useState(null);
  const [numberOfPages, setNumberOfPages] = useState(null);
  const [showDownloadBtn, setShowDownloadButton] = useState(false);
  const [loader, setLoader] = useState(false);
  const [pagesToSplit, setPagesToSplit] = useState({
    individualPages: [],
    pdfFiles: [],
  });
  const [signaturePopup, setSignaturePopup] = useState(false);
  const [pageSelection, setPageSelection] = useState("");
  const [manualPages, setManualPages] = useState("");
  const [error, setError] = useState("");

  const scrollToPreview = () => {
    const element = document.getElementById("preview-pdfs");
    if (element) {
      element.scrollIntoView({ behavior: "smooth" });
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setError("");
    const file = e.dataTransfer.files[0];
    setPdfFile(file);
    scrollToPreview();
  };

  const handlePdfUpload = (e) => {
    setError("");
    const file = e.target.files[0];
    setPdfFile(file);
    if (file) {
      scrollToPreview();
    }
  };

  const onDocumentLoadSuccess = ({ numPages }) => {
    setNumberOfPages(numPages);
  };

  const extractPages = async () => {
    setError("");
    setLoader(true);
    if (!pdfFile) return;
    if (!pageSelection) {
      setError("Please select pages or page range to extract");
      setLoader(false);
      return;
    }

    try {
      let pages = [];
      if (pageSelection === "even") {
        for (let i = 1; i <= numberOfPages; i++) {
          if (i % 2 === 0) {
            pages.push(i);
          }
        }
      }
      if (pageSelection === "odd") {
        for (let i = 1; i <= numberOfPages; i++) {
          if (i % 2 !== 0) {
            pages.push(i);
          }
        }
      }
      if (pageSelection === "manual") {
        if (!manualPages) {
          setError("Please enter pages to extract");
          setLoader(false);
          return;
        }
        let manualPagesArray = manualPages.split(",");
        for (let i = 0; i < manualPagesArray.length; i++) {
          if (manualPagesArray[i].includes("-")) {
            let pageRange = manualPagesArray[i].split("-");
            if (
              parseInt(parseInt(pageRange[0])) > parseInt(pageRange[1]) ||
              parseInt(pageRange[0]) < 1 ||
              parseInt(pageRange[1]) > numberOfPages
            ) {
              setError("Please enter valid page range");
              setLoader(false);
              return;
            }
            for (
              let i = parseInt(pageRange[0]);
              i <= parseInt(pageRange[1]);
              i++
            ) {
              pages.push(i);
            }
          } else {
            if (
              parseInt(parseInt(manualPagesArray[i])) < 1 ||
              parseInt(parseInt(manualPagesArray[i])) > parseInt(numberOfPages)
            ) {
              setError("Please enter valid page number");
              setLoader(false);
              return;
            }
            pages.push(parseInt(manualPagesArray[i]));
          }
        }
      }
      setPagesToSplit({ ...pagesToSplit, individualPages: pages });
      setShowDownloadButton(true);
      setLoader(false);
    } catch (error) {
      setError("Something went wrong. Please try again later");
      setLoader(false);
    }
  };

  const downloadExtractedPages = async () => {
    const pdfBytes = await pdfFile.arrayBuffer();
    const pdfDoc = await PDFDocument.load(pdfBytes);

    const pdfFiles = [];

    const promises = pagesToSplit.individualPages.map(async (page) => {
      const newPdf = await PDFDocument.create();
      const [pdfPage] = await newPdf.copyPages(pdfDoc, [page - 1]);
      newPdf.addPage(pdfPage);

      const pdfBytes = await newPdf.save();
      const blob = new Blob([pdfBytes], { type: "application/pdf" });
      const file = new File([blob], `page-${page}.pdf`, {
        type: "application/pdf",
      });
      pdfFiles.push(file);
    });

    await Promise.all(promises);

    const merger = new PDFMerger();
    for (const file of pdfFiles) {
      await merger.add(file);
    }

    const mergedPdf = await merger.saveAsBlob();
    setLoader(false);
    downloadPdf(mergedPdf, "extracted-pages.pdf");
  };

  const downloadPdf = (blob, fileName) => {
    const link = document.createElement("a");
    link.href = URL.createObjectURL(blob);
    link.download = fileName;
    link.click();
    setLoader(false);
    setSignaturePopup(true);
  };

  useEffect(() => {
    setError("");
    setShowDownloadButton(false);
  }, [pageSelection, pdfFile, manualPages]);

  return (
    <div className="w-full mt-20 sm:mt-24">
      <Head>
        <meta charSet="utf-8" />
        <title>Extract Pages From Pdf - BoloForms</title>
        <meta
          name="description"
          content="Extract pages from your PDF and save as a separate PDF."
        />
        <meta
          name="keywords"
          content="extract pages from pdf, extract pdf, extract pdf pages, extract pages from pdf online, extract pdf pages online, extract pdf pages free"
        />
        <meta name="author" content="BoloForms" />
        <meta name="viewport" content="width=device-width, initial-scale=1.0" />
        <link
          rel="canonical"
          href="https://www.bolosign.com/signature/pdf-tools/extract-pdf-pages/"
        />
        <meta
          property="og:title"
          content="Extract Pages From Pdf - BoloForms"
        />
        <meta
          property="og:description"
          content="Extract pages from your PDF and save as a separate PDF."
        />
        <meta
          property="og:url"
          content="https://www.bolosign.com/signature/pdf-tools/extract-pdf-pages/"
        />
        <meta property="og:site_name" content="BoloForms" />
        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="630" />
        <meta property="og:image:alt" content="BoloForms" />
        <meta property="og:type" content="website" />
        <meta property="og:locale" content="en_US" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:site" content="@BoloForms" />
        <meta name="twitter:creator" content="@BoloForms" />
        <meta
          name="twitter:title"
          content="Extract Pages From Pdf  - BoloForms"
        />
        <meta
          name="twitter:description"
          content="Extract pages from your PDF and save as a separate PDF."
        />
      </Head>

      <div className="">
        <div className="max-w-[1280px] mx-auto px-2 md:px-10 py-20 rounded-xl relative flex flex-col gap-20">
          <div className="text-violet-500 text-center">
            <h1 className="text-3xl md:text-6xl text-gray-800">
              Extract Pages From Pdf
            </h1>
            <p className="text-lg md:text-2xl mt-4 text-gray-600">
              The online PDF splitter tool allows you to extract pages from a
              PDF file for free.
            </p>
          </div>
          <div
            className="extraOutline p-4 bg-white bg-whtie mx-auto rounded-lg w-full md:w-[600px]"
            onDragEnter={(e) => e.preventDefault()}
            onDragOver={(e) => e.preventDefault()}
            onDragLeave={(e) => e.preventDefault()}
            onDrop={handleDrop}
          >
            <div className="file_upload px-2 md:px-20 py-5 md:py-14 relative border-4 border-dotted border-gray-300 rounded-lg">
              <FaFileUpload className="text-gray-400 w-20 md:w-32 mx-auto mb-6 md:mb-10 text-[80px] md:text-[120px]" />
              <div className="input_field flex flex-col w-full mx-auto text-center">
                <label>
                  <input
                    onChange={handlePdfUpload}
                    className="text-sm cursor-pointer w-36 hidden"
                    type="file"
                    accept=".pdf"
                  />
                  <div className="text bg-violet-600 text-white text-xl border border-gray-300 rounded-xl font-semibold cursor-pointer py-3 md:py-4 hover:bg-indigo-500">
                    Upload PDF File
                  </div>
                </label>

                <div className="title text-gray-500 uppercase text-lg md:text-xl">
                  or drop file here
                </div>
              </div>
            </div>
          </div>
        </div>
        <div
          id="preview-pdfs"
          className="max-w-[1280px] mx-auto flex flex-col gap-14 justify-center items-center"
        >
          {pdfFile && (
            <>
              <div className="w-full h-[500px] flex flex-wrap justify-center gap-10 md:border-2 md:px-10 md:py-14 md:rounded-xl overflow-y-scroll scrollbar-thin scrollbar-thumb-violet-500 scrollbar-rounded-[12px]">
                {Array.from(new Array(numberOfPages), (el, index) => (
                  <div
                    key={index}
                    className="border drop-shadow-2xl w-fit relative"
                  >
                    <Document
                      file={pdfFile}
                      onLoadSuccess={onDocumentLoadSuccess}
                    >
                      <Page
                        pageNumber={index + 1}
                        width={260}
                        renderTextLayer={false}
                        renderAnnotationLayer={false}
                      />
                    </Document>
                    <div className="absolute top-2 left-2 w-8 h-8 bg-violet-500 rounded-full text-xl text-white flex justify-center items-center cursor-pointer">
                      {index + 1}
                    </div>
                    {/* <div onClick={() => addOrRemovePage(index + 1)} className={`absolute top-2 right-2 w-8 h-8 ${pagesToSplit.individualPages.includes(index + 1) ? "bg-violet-500" : "bg-gray-400"} rounded-full text-xl text-white flex justify-center items-center cursor-pointer`}>
                                                <AiOutlineCheck />
                                        </div> */}
                  </div>
                ))}
              </div>
            </>
          )}
          {pdfFile && (
            <div id="split-pdfs" className="w-full">
              <div
                className={`flex justify-center items-center flex-wrap text-lg mb-4`}
              >
                <div
                  onClick={() => setPageSelection("even")}
                  className={`w-[200px] text-center py-2 px-4 cursor-pointer hover:opacity-90 border ${
                    pageSelection === "even"
                      ? "text-violet-500 border-violet-400"
                      : "text-gray-500 border-gray-400"
                  }`}
                >
                  All Even Pages
                </div>
                <div
                  onClick={() => setPageSelection("odd")}
                  className={`w-[200px] text-center py-2 px-4 cursor-pointer hover:opacity-90 border ${
                    pageSelection === "odd"
                      ? "text-violet-500 border-violet-400"
                      : "text-gray-500 border-gray-400"
                  }`}
                >
                  All Odd Pages
                </div>
                <div
                  onClick={() => setPageSelection("manual")}
                  className={`w-[200px] text-center py-2 px-4 cursor-pointer hover:opacity-90 border ${
                    pageSelection === "manual"
                      ? "text-violet-500 border-violet-400"
                      : "text-gray-500 border-gray-400"
                  }`}
                >
                  Specific Pages
                </div>
              </div>
              {pageSelection === "manual" && (
                <div className="text-lg mb-2 flex justify-center items-center">
                  <input
                    type="text"
                    className="border-2 outline-none h-[45px] w-[600px] text-gray-400 background-transparent px-2 py-2 rounded-md"
                    onChange={(e) => setManualPages(e.target.value)}
                    value={manualPages}
                    placeholder="Example : 1-5,7,9-11,13,15"
                    name=""
                    id=""
                  />
                </div>
              )}

              <div className="text-lg flex justify-center items-center h-10">
                <p className="text-red-500">{error}</p>
              </div>
              <button
                onClick={extractPages}
                className="bg-violet-600 mt-4 mx-auto w-full sm:w-[350px] py-4 text-xl rounded-lg font-semibold text-white flex items-center justify-center gap-4 hover:bg-violet-400"
              >
                {loader ? (
                  <div
                    class={`flex items-center text-white space-x-2 ${styles.loader}`}
                  >
                    <div class="">Downloading</div>
                    <div class="w-2 h-2 bg-white rounded-full animate-bounce"></div>
                    <div class="w-2 h-2 bg-white rounded-full animate-bounce"></div>
                    <div class="w-2 h-2 bg-white rounded-full animate-bounce"></div>
                  </div>
                ) : (
                  <>Extract Selected Pages</>
                )}
              </button>
              {showDownloadBtn && (
                <div className="w-full flex flex-col gap-3 justify-center items-center">
                  <div className="w-[2px] h-10 bg-gray-300 mt-2"></div>
                  <p className="text-violet-600 text-center text-xl">
                    Pdf Pages Extracted Successfully
                  </p>
                  <div className="w-[2px] h-10 bg-gray-300"></div>
                  <button
                    onClick={downloadExtractedPages}
                    className="bg-violet-600 w-full sm:w-[350px] py-4 text-xl rounded-lg font-semibold text-white flex items-center justify-center gap-4 hover:bg-violet-400 mx-auto"
                  >
                    Download Extracted Pages
                  </button>
                </div>
              )}
            </div>
          )}
        </div>

        {signaturePopup && (
          <SignaturePopup
            onPopupClose={() => setSignaturePopup(false)}
            source="EXTRACT_PDF_PAGES"
          />
        )}

        <div className="mt-20">
          <div className="mb-20">
            <PdfToolsLinks />
          </div>
          <Blog heading="PDF Split Tool FAQs" BlogData={BlogData} />
        </div>
      </div>
    </div>
  );
};

export default ExtractPdfPages;
